import type { Step } from './routine';

export type EngineStatus = 'idle' | 'running' | 'paused' | 'complete' | 'ended';
export interface EngineSnapshot {
  status: EngineStatus;
  index: number;
  step: Step;
  remainingMs: number;
  elapsedMs: number;
  totalRemainingMs: number;
  progress: number;
}
export interface EngineEvent { type: 'step' | 'complete' | 'state'; snapshot: EngineSnapshot }

type Listener = (event: EngineEvent) => void;

export class WorkoutEngine {
  private steps: Step[];
  private now: () => number;
  private listener: Listener;
  private index = 0;
  private status: EngineStatus = 'idle';
  private endsAt = 0;
  private remainingAtPause = 0;

  constructor(steps: Step[], listener: Listener = () => undefined, now: () => number = () => Date.now()) {
    if (!steps.length) throw new Error('Workout needs at least one step');
    this.steps = steps;
    this.listener = listener;
    this.now = now;
    this.remainingAtPause = steps[0]!.durationMs;
  }

  getSnapshot(at = this.now()): EngineSnapshot {
    const step = this.steps[this.index]!;
    const remainingMs = this.status === 'running'
      ? Math.max(0, this.endsAt - at)
      : this.status === 'complete'
        ? 0
        : this.remainingAtPause;
    const elapsedMs = Math.max(0, step.durationMs - remainingMs);
    const before = this.steps.slice(0, this.index).reduce((n, item) => n + item.durationMs, 0);
    const total = this.steps.reduce((n, item) => n + item.durationMs, 0);
    const elapsedTotal = before + elapsedMs;
    const totalRemainingMs = Math.max(0, total - elapsedTotal);
    return {
      status: this.status, index: this.index, step, remainingMs, elapsedMs,
      totalRemainingMs, progress: total ? Math.min(1, elapsedTotal / total) : 1,
    };
  }

  start(at = this.now()): EngineSnapshot {
    if (this.status === 'running') return this.getSnapshot(at);
    if (this.status === 'complete' || this.status === 'ended') this.restart(at);
    this.status = 'running';
    this.endsAt = at + this.remainingAtPause;
    this.emit('state', at);
    return this.getSnapshot(at);
  }

  pause(at = this.now()): EngineSnapshot {
    if (this.status !== 'running') return this.getSnapshot(at);
    this.sync(at);
    if (this.status === 'running') {
      this.remainingAtPause = Math.max(0, this.endsAt - at);
      this.status = 'paused';
      this.emit('state', at);
    }
    return this.getSnapshot(at);
  }

  resume(at = this.now()): EngineSnapshot {
    return this.start(at);
  }

  sync(at = this.now()): EngineSnapshot {
    if (this.status !== 'running') return this.getSnapshot(at);
    let changed = false;
    while (this.status === 'running' && at >= this.endsAt) {
      const overshoot = at - this.endsAt;
      if (this.index >= this.steps.length - 2) {
        this.index = this.steps.length - 1;
        this.remainingAtPause = 0;
        this.status = 'complete';
        this.endsAt = at;
        this.emit('complete', at);
        return this.getSnapshot(at);
      }
      this.index += 1;
      this.remainingAtPause = this.steps[this.index]!.durationMs;
      this.endsAt = at + Math.max(0, this.remainingAtPause - overshoot);
      changed = true;
    }
    if (changed) this.emit('step', at);
    return this.getSnapshot(at);
  }

  next(at = this.now()): EngineSnapshot {
    if (this.index >= this.steps.length - 2) {
      this.index = this.steps.length - 1;
      this.status = 'complete';
      this.remainingAtPause = 0;
      this.emit('complete', at);
      return this.getSnapshot(at);
    }
    this.index += 1;
    this.remainingAtPause = this.steps[this.index]!.durationMs;
    if (this.status === 'running') this.endsAt = at + this.remainingAtPause;
    this.emit('step', at);
    return this.getSnapshot(at);
  }

  previous(at = this.now()): EngineSnapshot {
    this.index = Math.max(0, this.index - 1);
    this.remainingAtPause = this.steps[this.index]!.durationMs;
    if (this.status === 'complete' || this.status === 'ended') this.status = 'paused';
    if (this.status === 'running') this.endsAt = at + this.remainingAtPause;
    this.emit('step', at);
    return this.getSnapshot(at);
  }

  jump(index: number, at = this.now()): EngineSnapshot {
    if (!Number.isInteger(index) || index < 0 || index >= this.steps.length - 1) return this.getSnapshot(at);
    this.index = index;
    this.remainingAtPause = this.steps[index]!.durationMs;
    if (this.status === 'complete' || this.status === 'ended') this.status = 'paused';
    if (this.status === 'running') this.endsAt = at + this.remainingAtPause;
    this.emit('step', at);
    return this.getSnapshot(at);
  }

  restart(at = this.now()): EngineSnapshot {
    this.index = 0;
    this.status = 'idle';
    this.remainingAtPause = this.steps[0]!.durationMs;
    this.endsAt = 0;
    this.emit('state', at);
    return this.getSnapshot(at);
  }

  end(at = this.now()): EngineSnapshot {
    const wasRunning = this.status === 'running';
    this.remainingAtPause = Math.max(0, wasRunning ? this.endsAt - at : this.remainingAtPause);
    this.status = 'ended';
    this.emit('state', at);
    return this.getSnapshot(at);
  }

  private emit(type: EngineEvent['type'], at: number): void {
    this.listener({ type, snapshot: this.getSnapshot(at) });
  }
}
